// A document's small picture (PRD 21 §6.2, §6.3), or its kind's icon when it has
// none: a PDF, a HEIC, anything uploaded before thumbnails were drawn.
//
// Through the api like every stored file, behind sign-in, so an <img> cannot
// point at it directly. A document's thumbnail never changes, so each is
// fetched once per tab and kept as an object URL; a failure is forgotten, so
// the next page to show it tries again.

import React, { useEffect, useState } from 'react';
import { YStack } from 'tamagui';
import { FileText, Image as ImageIcon, Paperclip } from 'lucide-react';
import { businessDocumentService } from '../../../services/businessDocumentService';
import { useSemanticColors } from '../common/semanticColors';

export interface ThumbnailDocument {
  id: string;
  filename: string;
  content_type: string;
  has_thumbnail?: boolean;
}

const urls = new Map<string, Promise<string | null>>();

export function thumbnailUrl(documentId: string): Promise<string | null> {
  let url = urls.get(documentId);
  if (!url) {
    url = Promise.resolve()
      .then(() => businessDocumentService.thumbnail(documentId))
      .then(blob => URL.createObjectURL(blob))
      .catch(() => {
        urls.delete(documentId);
        return null;
      });
    urls.set(documentId, url);
  }
  return url;
}

/** For tests: every page load starts with none fetched. */
export function forgetThumbnails(): void {
  urls.clear();
}

export const DocumentThumbnail: React.FC<{ document: ThumbnailDocument; size?: number }> = ({
  document,
  size = 48,
}) => {
  const semantic = useSemanticColors();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setUrl(null);
    if (document.has_thumbnail) {
      void thumbnailUrl(document.id).then(found => {
        if (live) setUrl(found);
      });
    }
    return () => {
      live = false;
    };
  }, [document.id, document.has_thumbnail]);

  if (url) {
    return (
      <img
        src={url}
        alt={document.filename}
        width={size}
        height={size}
        style={{ objectFit: 'cover', borderRadius: 6, display: 'block', flexShrink: 0 }}
      />
    );
  }

  const Icon = document.content_type === 'application/pdf'
    ? FileText
    : document.content_type.startsWith('image/')
      ? ImageIcon
      : Paperclip;
  return (
    <YStack
      width={size}
      height={size}
      flexShrink={0}
      borderRadius={6}
      backgroundColor="$bgRaised"
      alignItems="center"
      justifyContent="center"
      role="img"
      aria-label={document.filename}
    >
      <Icon size={Math.round(size / 2)} color={semantic.muted} />
    </YStack>
  );
};

export default DocumentThumbnail;
